import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import type {
  ProjectCategoryOptionCreate,
  ProjectCategoryOptionUpdate,
  ProjectCategoryOption,
} from '@/lib/schemas';

export const projectCategoryKeys = {
  all: ['project-categories'] as const,
  lists: () => [...projectCategoryKeys.all, 'list'] as const,
  list: (includeInactive: boolean) => [...projectCategoryKeys.lists(), includeInactive ? 'all' : 'active'] as const,
  details: () => [...projectCategoryKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectCategoryKeys.details(), id] as const,
};

export function useProjectCategoryOptions(includeInactive = false) {
  return useQuery({
    queryKey: projectCategoryKeys.list(includeInactive),
    queryFn: async () => {
      const response = await api.projectCategories.list({ include_inactive: includeInactive });
      return response.categories;
    },
    staleTime: 5 * 60_000, // 5 min — reference data, rarely changes
  });
}

export function useCreateProjectCategoryOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ProjectCategoryOptionCreate) => {
      const response = await api.projectCategories.create(data);
      return response.category;
    },
    onSuccess: (newCategory: ProjectCategoryOption) => {
      queryClient.invalidateQueries({ queryKey: projectCategoryKeys.lists() });
      toast.success(`Category "${newCategory.value}" created`);
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Failed to create project category');
    },
  });
}

export function useUpdateProjectCategoryOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ProjectCategoryOptionUpdate }) => {
      const response = await api.projectCategories.update(id, data);
      return response.category;
    },
    onSuccess: (updated: ProjectCategoryOption) => {
      queryClient.invalidateQueries({ queryKey: projectCategoryKeys.list(true) });
      queryClient.invalidateQueries({ queryKey: projectCategoryKeys.list(false) });
      toast.success(`Category "${updated.value}" updated`);
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Failed to update project category');
    },
  });
}

// R4: Dedicated mutation for saving default_sections with the spec-required toast message.
// Uses the same PUT endpoint as useUpdateProjectCategoryOption but shows
// "Default sections updated for <Name>." instead of the generic category-updated toast.
export function useUpdateCategoryDefaultSections() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, defaultSections }: { id: string; defaultSections: string[] }) => {
      const response = await api.projectCategories.update(id, { default_sections: defaultSections });
      return response.category;
    },
    onSuccess: (updated: ProjectCategoryOption) => {
      queryClient.invalidateQueries({ queryKey: projectCategoryKeys.list(true) });
      queryClient.invalidateQueries({ queryKey: projectCategoryKeys.list(false) });
      toast.success(`Default sections updated for ${updated.value}.`);
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Failed to update default sections');
    },
  });
}

export function useDeleteProjectCategoryOption() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await api.projectCategories.delete(id);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: projectCategoryKeys.lists() });
      toast.success('Category archived');
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Failed to archive category');
    },
  });
}
